import { useState, useMemo, useEffect, useCallback } from "react";
import { CohereTable, convertHealthPlanEnumToName, TertiaryButton } from "@coherehealth/common";
import { User } from "@coherehealth/qm-api";
import { Box, Typography } from "@mui/material";
import { useUserManagementData } from "./useUserManagementData";
import { getUserTableColumns, isUserRowExpandable } from "./userTableColumns";

interface UserManagementTableProps {
  searchQuery: string;
  onUserCountChange: (count: number) => void;
  onLoadingChange: (loading: boolean) => void;
  onErrorChange: (hasError: boolean) => void;
  onEditUser: (user: User) => void;
  /** Hands the data refetch fn up to the parent so it can refresh the grid without remounting it. */
  onRefetchReady?: (refetch: () => void) => void;
}

export default function UserManagementTable({
  searchQuery,
  onUserCountChange,
  onLoadingChange,
  onErrorChange,
  onEditUser,
  onRefetchReady,
}: UserManagementTableProps) {
  const { users, loading, error, refetch, totalCount } = useUserManagementData(searchQuery || undefined);

  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDesc, setSortDesc] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set());

  // Notify parent of count and loading changes
  useEffect(() => {
    onUserCountChange(totalCount);
  }, [totalCount, onUserCountChange]);

  useEffect(() => {
    onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    onErrorChange(Boolean(error));
  }, [error, onErrorChange]);

  // Expose refetch upward so the parent can refresh in place (no remount) after a save.
  useEffect(() => {
    onRefetchReady?.(refetch);
  }, [refetch, onRefetchReady]);

  // Clear selection and expansion when data changes (search results, refetch)
  useEffect(() => {
    setSelectedIndexes([]);
    setExpandedUserIds(new Set());
  }, [users]);

  // Sort users client-side
  const sortedUsers = useMemo(() => {
    if (!users.length) {
      return users;
    }

    return [...users].sort((a, b) => {
      let aVal: string = "";
      let bVal: string = "";

      switch (sortBy) {
        case "name":
          aVal = a.name ?? "";
          bVal = b.name ?? "";
          break;
        case "opsGroup":
          aVal = convertHealthPlanEnumToName(a.opsGroup) || "";
          bVal = convertHealthPlanEnumToName(b.opsGroup) || "";
          break;
        case "licensedStates":
          aVal = a.licensedStates?.join(", ") ?? "";
          bVal = b.licensedStates?.join(", ") ?? "";
          break;
        default:
          return 0;
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDesc ? -cmp : cmp;
    });
  }, [users, sortBy, sortDesc]);

  const handleSort = useCallback((columnName: string, sortAscending: boolean) => {
    setSortBy(columnName);
    setSortDesc(!sortAscending);
    setSelectedIndexes([]);
  }, []);

  const handleRowSelect = useCallback((index: number, isSelected: boolean) => {
    setSelectedIndexes((prev) => (isSelected ? [...prev, index] : prev.filter((i) => i !== index)));
  }, []);

  const handleSelectAll = useCallback(
    (isSelected: boolean) => {
      setSelectedIndexes(isSelected ? sortedUsers.map((_, i) => i) : []);
    },
    [sortedUsers]
  );

  const handleToggleExpand = useCallback((user: User) => {
    setExpandedUserIds((prev) => {
      const next = new Set(prev);
      const id = user.id ?? "";
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // columns recreated every render — CohereTable rows re-render naturally (no React.memo,
  // key={index}) so no spread/tableData trick needed for expansion to propagate.
  const columns = getUserTableColumns({ onEditUser, onToggleExpand: handleToggleExpand, expandedUserIds });

  if (error && !loading) {
    return (
      <Box sx={{ width: "100%", textAlign: "center", py: 4 }} data-testid="user-management-table">
        <Typography color="error" variant="body1" sx={{ mb: 2 }}>
          Failed to load users: {error}
        </Typography>
        <TertiaryButton onClick={refetch} data-testid="retry-btn">
          Retry
        </TertiaryButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        // Hover-only actions: hide edit/expand icons, show on row hover
        "& .actions-hover": { opacity: 0, transition: "opacity 0.15s" },
        '& [data-testid^="table-row-"]:hover .actions-hover': { opacity: 1 },
        // Hover-only row checkboxes: hide per-row checkboxes, show on hover
        // Header checkbox (select-all) stays visible
        '& [data-testid^="table-row-"] .MuiCheckbox-root': { opacity: 0, transition: "opacity 0.15s" },
        '& [data-testid^="table-row-"]:hover .MuiCheckbox-root': { opacity: 1 },
        // Column header text color: teal per Figma (node 8339:6788).
        // NOTE: Uses MUI-internal selectors — no CohereTable prop exists for header color.
        // If CohereTable markup changes, these selectors need updating.
        '& [data-testid="default-header"] .MuiTableSortLabel-root': { color: "#039ec3" },
        '& [data-testid="default-header"] .MuiTypography-body1': { color: "#039ec3" },
        // Row-to-row gap: 12px per Figma (rows at y=36,88,140 → gap=12px)
        // Row position: relative so actions can be absolutely pinned to right edge
        '& [data-testid^="table-row-"] .MuiCard-root': { marginBottom: "12px", position: "relative" },
        // Rows that can't expand (no truncated chips) aren't clickable: drop the pointer cursor.
        // The chevron renders the .expandable-row marker only for expandable rows.
        '& [data-testid^="table-row-"] .MuiCard-root:not(:has(.expandable-row))': { cursor: "default" },
      }}
      data-testid="user-management-table"
    >
      <CohereTable<User>
        loading={loading}
        tableColumns={columns}
        data={sortedUsers}
        sortingColumnName={sortBy}
        sortingAscending={!sortDesc}
        onSortChange={handleSort}
        paginated
        defaultRowsPerPage={25}
        cardHeight="short"
        customTableRowPadding="8px 24px"
        rowHoverShadow="0px 4px 8px 0px rgba(21, 21, 21, 0.16)"
        hideRowHoverBorderColor
        rowActiveBackgroundColor="#F5F5F5"
        selectableRows
        selectedRows={selectedIndexes}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        onRowClick={(user: User) => {
          // Rows with nothing truncated have nothing to reveal, so a click does nothing.
          if (isUserRowExpandable(user)) {
            handleToggleExpand(user);
          }
        }}
        headerMargin="0px 0px 12px 0px"
        emptyListCaption="No users found"
      />
    </Box>
  );
}
